import * as THREE from 'three';
import { uniform } from 'three/tsl';
import type { StyleConfig } from './StyleConfig';
import type { RendererMode } from './Renderer';

const makeNodeUniform = uniform as unknown as (
  value: unknown,
) => ReturnType<typeof uniform> & Record<string, any>;

export type ValueBucket = 'fg' | 'mg' | 'bg';

export interface ValueCompositionUniforms {
  uValueExposure: { value: number };
}

export const VALUE_BUCKETS: readonly ValueBucket[] = ['fg', 'mg', 'bg'] as const;

const SHARED_EXPOSURES: Record<ValueBucket, { value: number }> = {
  fg: { value: 1.0 },
  mg: { value: 0.95 },
  bg: { value: 1.15 },
};

// One TSL uniform node per bucket. Created lazily on first WebGPU material
// to avoid initialising TSL machinery on the WebGL-only path. Mutating
// `SHARED_EXPOSURES[bucket].value` keeps both node and onBeforeCompile
// uniforms in sync; we just need the node to read from the same `.value`.
const BUCKET_NODES: Partial<Record<ValueBucket, ReturnType<typeof makeNodeUniform>>> = {};
function bucketExposureNode(bucket: ValueBucket): ReturnType<typeof makeNodeUniform> {
  let node = BUCKET_NODES[bucket];
  if (!node) {
    node = makeNodeUniform(SHARED_EXPOSURES[bucket].value);
    BUCKET_NODES[bucket] = node;
  }
  return node;
}

export function applyValueCompositionStyle(style: StyleConfig): void {
  SHARED_EXPOSURES.fg.value = style.artDirection.valueComposition.fgExposure;
  SHARED_EXPOSURES.mg.value = style.artDirection.valueComposition.mgExposure;
  SHARED_EXPOSURES.bg.value = style.artDirection.valueComposition.bgExposure;
  // Push into the WebGPU TSL uniform nodes (when they exist). The WebGL
  // onBeforeCompile path reads through the same SHARED_EXPOSURES record.
  for (const bucket of VALUE_BUCKETS) {
    const node = BUCKET_NODES[bucket];
    if (node) node.value = SHARED_EXPOSURES[bucket].value;
  }
}

export function valueExposureUniform(bucket: ValueBucket = 'mg'): ValueCompositionUniforms {
  return { uValueExposure: SHARED_EXPOSURES[bucket] };
}

export function tagValueBucket(object: THREE.Object3D, bucket: ValueBucket): void {
  object.userData.valueBucket = bucket;
}

export function tagValueBucketDeep(object: THREE.Object3D, bucket: ValueBucket): void {
  object.traverse((child) => {
    child.userData.valueBucket = bucket;
  });
}

export function valueBucketForObject(object: THREE.Object3D): ValueBucket {
  const bucket = object.userData.valueBucket;
  return bucket === 'fg' || bucket === 'mg' || bucket === 'bg' ? bucket : 'mg';
}

export function installValueComposition(
  material: THREE.Material | null | undefined,
  bucket: ValueBucket = 'mg',
  rendererMode: RendererMode = 'webgl2',
): void {
  if (!material) return;
  material.userData ??= {};
  if (material.userData.valueCompositionInstalled) return;
  material.userData.valueCompositionInstalled = true;
  material.userData.valueCompositionBucket = bucket;
  material.userData.valueCompositionUniforms = valueExposureUniform(bucket);
  if (rendererMode === 'webgpu') {
    // WebGPU node-material path: multiply existing colorNode + emissiveNode
    // by the bucket exposure. This applies before fog mixing (TSL fog runs
    // post-output), which is one chunk earlier than WebGL's pre-tonemapping
    // hook — the visible difference is negligible at the 0.95–1.15 range
    // we use, while the upside (parity contrast separation between fg/mg/bg)
    // is large.
    const exposure = bucketExposureNode(bucket);
    const nodeMat = material as THREE.Material & {
      colorNode?: { mul: (n: unknown) => unknown };
      emissiveNode?: { mul: (n: unknown) => unknown };
    };
    if (nodeMat.colorNode) nodeMat.colorNode = nodeMat.colorNode.mul(exposure) as typeof nodeMat.colorNode;
    if (nodeMat.emissiveNode) nodeMat.emissiveNode = nodeMat.emissiveNode.mul(exposure) as typeof nodeMat.emissiveNode;
    material.userData.valueCompositionWebGPUInstalled = true;
    material.userData.valueCompositionWebGPUFallback = true;
    return;
  }

  const prev = typeof material.onBeforeCompile === 'function' ? material.onBeforeCompile : undefined;
  material.onBeforeCompile = (shader, renderer) => {
    prev?.(shader, renderer);
    Object.assign(shader.uniforms, valueExposureUniform(bucket));
    if (!shader.fragmentShader.includes('uniform float uValueExposure;')) {
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <common>',
        '#include <common>\nuniform float uValueExposure;',
      );
    }
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <tonemapping_fragment>',
      'gl_FragColor.rgb *= uValueExposure;\n#include <tonemapping_fragment>',
    );
  };
  // Stable cache key keyed by bucket so Three.js dedupes the compiled program
  // across all materials in the same bucket. Without this, every material
  // produces a unique program even though the injected GLSL is identical,
  // causing a synchronous shader compile stall when each first becomes
  // visible. The bucket is part of the key because the per-bucket uniform
  // value (fg/mg/bg exposure) is part of the program's effective behavior.
  const matWithKey = material as THREE.Material & { customProgramCacheKey?: () => string };
  const prevCacheKey = matWithKey.customProgramCacheKey?.bind(matWithKey);
  matWithKey.customProgramCacheKey = () => `${prevCacheKey ? prevCacheKey() : ''}|valueComp-${bucket}`;
  material.needsUpdate = true;
}

export function installValueCompositionForObject(object: THREE.Object3D, fallbackBucket: ValueBucket = 'mg'): void {
  object.traverse((child) => {
    const taggedBucket = child.userData.valueBucket;
    const bucket = taggedBucket === 'fg' || taggedBucket === 'mg' || taggedBucket === 'bg'
      ? taggedBucket
      : fallbackBucket;
    const mesh = child as THREE.Object3D & { material?: THREE.Material | Array<THREE.Material | null> | null };
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const material of materials) installValueComposition(material, bucket);
  });
}
